import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { useCatalogoExamesConsultaControllerObter } from '@/api/generated/catalogo-exames/catalogo-exames';
import {
  getExamesControllerListarQueryKey,
  useExamesControllerLancarResultados,
} from '@/api/generated/exames/exames';
import { ApiError } from '@/api/http-client';
import type { ExameResponse } from '@/api/generated/model';
import { CLASSE_INTERPRETACAO, ROTULO_INTERPRETACAO } from '@/features/exames/rotulos';
import { EstadoCarregando, EstadoErro } from '@/shared/components/estados';
import { CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { toast } from '@/shared/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  exame: ExameResponse;
  /** Sem `resultados.editar`, o dialog vira só leitura. */
  podeEditar: boolean;
}

interface Rascunho {
  valorNumerico: string;
  valorTexto: string;
  observacao: string;
}

const VAZIO: Rascunho = { valorNumerico: '', valorTexto: '', observacao: '' };

/**
 * Lançamento e leitura de resultados, por analito.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  A INTERPRETAÇÃO É DO SERVIDOR. Ele escolhe a faixa pela espécie, sexo e
 *  idade do paciente, compara e persiste. Esta tela **exibe** `interpretacao` e
 *  `faixaDeReferencia` como vieram — nunca recalcula.
 *
 *  A V1 fazia o oposto: `Resultados.tsx` derivava a interpretação a cada
 *  render, e o que aparecia na tela podia divergir do que estava gravado.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ **Só vai para o servidor o analito que tem algum valor preenchido.** O
 * lançamento é um upsert por analito, e o backend vira o exame para CONCLUÍDO
 * quando a contagem de resultados alcança a de analitos. Mandar linha vazia
 * criaria resultado em branco e daria o exame por concluído sem ninguém ter
 * medido nada.
 *
 * A unidade não é enviada: omitida, o servidor herda a do analito no catálogo.
 */
export function ResultadosDialogo({ aberto, aoFechar, exame, podeEditar }: Props) {
  const queryClient = useQueryClient();
  const lancar = useExamesControllerLancarResultados();
  const [rascunhos, setRascunhos] = useState<Record<string, Rascunho>>({});

  const { data: catalogo, isPending, isError, refetch } = useCatalogoExamesConsultaControllerObter(
    exame.catalogoExameId,
    { query: { enabled: aberto } },
  );

  const jaLancado = new Map(exame.resultados.map((r) => [r.analitoId, r]));

  useEffect(() => {
    if (!aberto) return;

    const inicial: Record<string, Rascunho> = {};
    for (const resultado of exame.resultados) {
      inicial[resultado.analitoId] = {
        valorNumerico: resultado.valorNumerico?.toString() ?? '',
        valorTexto: resultado.valorTexto ?? '',
        observacao: resultado.observacao ?? '',
      };
    }
    setRascunhos(inicial);
  }, [aberto, exame]);

  function editar(analitoId: string, campo: keyof Rascunho, valor: string) {
    setRascunhos((atual) => ({
      ...atual,
      [analitoId]: { ...(atual[analitoId] ?? VAZIO), [campo]: valor },
    }));
  }

  async function salvar() {
    const resultados = Object.entries(rascunhos)
      .filter(([, r]) => r.valorNumerico.trim() || r.valorTexto.trim() || r.observacao.trim())
      .map(([analitoId, r]) => ({
        analitoId,
        valorNumerico: r.valorNumerico.trim() ? Number(r.valorNumerico.replace(',', '.')) : null,
        valorTexto: r.valorTexto.trim() || null,
        observacao: r.observacao.trim() || null,
      }));

    if (resultados.length === 0) {
      toast.error('Preencha ao menos um analito.');
      return;
    }

    const invalido = resultados.find((r) => r.valorNumerico !== null && Number.isNaN(r.valorNumerico));
    if (invalido) {
      toast.error('Há valor numérico inválido. Use apenas números.');
      return;
    }

    try {
      await lancar.mutateAsync({ id: exame.id, data: { resultados } });
      await queryClient.invalidateQueries({ queryKey: getExamesControllerListarQueryKey() });
      toast.success('Resultados lançados.');
      aoFechar();
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível lançar os resultados.');
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            Resultados — {exame.exameNome}
          </DialogTitle>
          <DialogDescription>
            {exame.pacienteNome} · {exame.resultados.length} de {exame.totalAnalitos} exames
            lançados.
          </DialogDescription>
        </DialogHeader>

        {isPending ? (
          <EstadoCarregando rotulo="Carregando analitos..." />
        ) : isError || !catalogo ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : (
          <div className="overflow-x-auto max-h-[26rem]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Exames</TableHead>
                  <TableHead className="w-32">Valor</TableHead>
                  <TableHead className="w-40">Texto</TableHead>
                  <TableHead>Referência</TableHead>
                  <TableHead>Interpretação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {catalogo.analitos.map((analito) => {
                  const rascunho = rascunhos[analito.id] ?? VAZIO;
                  const gravado = jaLancado.get(analito.id);

                  return (
                    <TableRow key={analito.id}>
                      <TableCell>
                        <span className="font-medium text-foreground">{analito.nome}</span>
                        {analito.unidade && (
                          <p className="text-xs text-muted-foreground mt-0.5">{analito.unidade}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Input
                          value={rascunho.valorNumerico}
                          onChange={(e) => editar(analito.id, 'valorNumerico', e.target.value)}
                          disabled={!podeEditar}
                          inputMode="decimal"
                          className={CLASSE_CONTROLE}
                          aria-label={`Valor de ${analito.nome}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={rascunho.valorTexto}
                          onChange={(e) => editar(analito.id, 'valorTexto', e.target.value)}
                          disabled={!podeEditar}
                          className={CLASSE_CONTROLE}
                          aria-label={`Texto de ${analito.nome}`}
                        />
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                        {gravado?.faixaDeReferencia ?? '—'}
                      </TableCell>
                      <TableCell>
                        {gravado?.interpretacao ? (
                          <Badge
                            variant="outline"
                            className={CLASSE_INTERPRETACAO[gravado.interpretacao]}
                          >
                            {ROTULO_INTERPRETACAO[gravado.interpretacao] ?? gravado.interpretacao}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={aoFechar}>
            {podeEditar ? 'Cancelar' : 'Fechar'}
          </Button>
          {podeEditar && (
            <Button onClick={() => void salvar()} disabled={lancar.isPending}>
              {lancar.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Lançar resultados
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
